
// The number of days after which an open task is considered overdue.
const OVERDUE_THRESHOLD_DAYS = 7;

/**
 * Checks if a maintenance task is overdue based on its creation date and status.
 * A task is overdue if it is not completed and was created more than `OVERDUE_THRESHOLD_DAYS` ago.
 * @param {string} dateString - The ISO date string of when the task was created.
 * @param {string} status - The current status of the task.
 * @returns {boolean} - True if the task is overdue, false otherwise.
 */
export const isOverdue = (dateString: string, status: string): boolean => {
  // Completed tasks can never be overdue.
  if (status === 'completed') {
    return false;
  }

  const taskDate = new Date(dateString);
  const now = new Date();
  
  // Calculate the difference in days between now and the task creation date.
  const diffTime = now.getTime() - taskDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return diffDays > OVERDUE_THRESHOLD_DAYS;
};

/**
 * Formats a date string into a human-readable "days ago" format, e.g., "(Today)", "(1 day ago)".
 * @param {string} dateString - The ISO date string to format.
 * @returns {string} - The formatted string.
 */
export const formatDateAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  
  // To ignore time and compare dates only, we set the time to the start of the day.
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const diffTime = startOfToday.getTime() - startOfDate.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return ''; // Return empty string for future dates.
  }

  if (diffDays === 0) {
    return '(Today)';
  }

  if (diffDays === 1) {
    return '(1 day ago)';
  }

  return `(${diffDays} days ago)`;
};

/**
 * Formats a date string into a precise relative time string (e.g., "5 minutes ago", "2 hours ago").
 * @param {string} dateString - The ISO date string to format.
 * @returns {string} - The formatted relative time string.
 */
export const formatTimeAgo = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  // Calculate intervals for years, months, days, etc.
  let interval = seconds / 31536000;
  if (interval > 1) {
    const years = Math.floor(interval);
    return `${years} year${years > 1 ? 's' : ''} ago`;
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    const months = Math.floor(interval);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  }
  interval = seconds / 86400;
  if (interval > 1) {
    const days = Math.floor(interval);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }
  interval = seconds / 3600;
  if (interval > 1) {
    const hours = Math.floor(interval);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  }
  interval = seconds / 60;
  if (interval > 1) {
    const minutes = Math.floor(interval);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  }
  if (seconds < 10) {
    return "just now";
  }
  return `${Math.floor(seconds)} second${seconds > 1 ? 's' : ''} ago`;
};
